import CloseButton from 'Components/Common/CloseButton'
import FormField from 'Components/Common/FormField'
import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Collapse, FormGroup, Input, Label, Offcanvas, OffcanvasBody, OffcanvasHeader } from 'reactstrap'
import { GigType } from 'types/campaign/collaborations'
import { CampaignGig } from 'types/campaign/milestones'
import { SelectedStore } from '../milestone/gigs/Stores'
import CampaignGigComponent from '../milestone/components/CampaignGig'
import { add, noop } from 'lodash'
import 'react-perfect-scrollbar/dist/css/styles.css';
import PerfectScrollbar from 'react-perfect-scrollbar'
import { createCampaignGig, updateCampaignGig } from 'services/api/campaign'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import AddressForm from './AddressForm'
import AddressDetails from './AddressDetail'
import CampaignGigOptions from './CampaignGigOptions'
import { useGetCampaignDataById } from 'slices/campaigns/selector'

interface AddCampaignGigProps {
    selectedGigId?: number,
    addCampaign: (gigId: number) => void,
    campaignGigs: CampaignGig[],
    onClose: () => void,
    isOpen: boolean,
    gigType: GigType,
    campaignGigIds: (string | number)[]
}

const AddCampaignGig: React.FC<AddCampaignGigProps> = ({ selectedGigId, addCampaign, campaignGigs, gigType, onClose, isOpen, campaignGigIds }) => {

    const [campaignGig, setCampaignGig] = useState<Omit<CampaignGig, 'id'>>({ name: '', gig_type_id: gigType?.id, properties: {} });
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const { campaignId } = useParams();
    const campaign = useGetCampaignDataById(campaignId!);
    const currency = campaign?.currency;
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [showNotAttached, toggleNotAttachedList] = useState(true);

    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (selectedGigId) {
            const gig = campaignGigs.find(g => g.id === selectedGigId);
            if (gig) {
                setCampaignGig(gig);
            } else {
                setCampaignGig({ name: '', gig_type_id: gigType?.id, properties: {} });
            }
        } else {
            setCampaignGig({ name: '', gig_type_id: gigType?.id, properties: {} });
        }
    }, [campaignGigs, selectedGigId, gigType]);

    const handleGigChange = (key: string, value: any) => {
        const updatedGig = { ...campaignGig, [key]: value }
        setCampaignGig(updatedGig);
    }

    const handleGigPropertyChange = (key: string, value: any) => {
        const updatedCampaignGig = { ...campaignGig, properties: { ...campaignGig.properties, [key]: value } }
        setCampaignGig(updatedCampaignGig);
    }


    const notAttachedGigs = useMemo(() => {
        return campaignGigs.filter(gig => !campaignGigIds.includes(gig.id) && gig.gig_type_id === gigType?.id);
    }, [campaignGigs, campaignGigIds, gigType]);


    const validateForm = () => {
        setErrors({});
        const validation_errors: Record<string, string> = {};
        if (Array.isArray(gigType?.properties)) {
            for (let property of gigType.properties) {
                if (property.mandatory && (campaignGig.properties[property.key] === null || campaignGig.properties[property.key] === undefined || campaignGig.properties[property.key] === '')) {
                    validation_errors[property.key] = `${property.label} is required.`
                }
            }
        }
        setErrors(validation_errors);
        return validation_errors;

    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedAgencyId || !campaignId) return;

        const validationErrors = validateForm();

        if (Object.values(validationErrors).length > 0) {
            return false;
        }



        setLoading(true);
        if (selectedGigId) {
            // Edit existing gig
            const updatedGig = { ...campaignGig, id: selectedGigId };
            try {
                await updateCampaignGig(selectedAgencyId, campaignId, selectedGigId, updatedGig);
                toast.success(`${gigType?.object_name} updated successfully.`);
                addCampaign(selectedGigId);
            } catch (error: any) {
                console.log(error);
                toast.error(error?.response?.data?.message ?? `Failed to update ${gigType?.object_name}.`);
            } finally {
                setLoading(false);
            }

        } else {
            // Add new gig
            try {
                const gig = await createCampaignGig(selectedAgencyId, campaignId, campaignGig);
                toast.success(`${gigType?.object_name} added successfully.`);
                addCampaign(gig.id);
            } catch (error: any) {
                console.log(error);
                toast.error(error?.response?.data?.message ?? `Failed to add ${gigType?.object_name}.`);
            } finally {
                setLoading(false);
            }
        }
    }

    const addressProperty = useMemo(() => {
        return gigType?.properties?.find((property) => property.key === 'address');
    }, [gigType])

    const clearAddress = (key: string) => {
        if (gigType?.slug === 'store_visit') {
            const updatedCampaignGig = { ...campaignGig, name: '', properties: { ...campaignGig.properties, business_hours: '', address: '', phone: '' } }
            setCampaignGig(updatedCampaignGig);
        } else {
            handleGigPropertyChange(key, '');
        }
    }


    return (
        <Offcanvas isOpen={isOpen} toggle={onClose} direction='end' id="add-campaign-gig-offcanvas">
            <CloseButton onClose={onClose} />
            {
                isOpen &&
                <div>
                    <OffcanvasBody className='p-0'>
                        <div className="payments-offcanvas-bg"></div>
                        <PerfectScrollbar className='add-campaign-gig-form p-4 pb-0' style={{ maxHeight: 'calc(100vh - 150px)' }}>

                            {
                                addressProperty &&
                                (
                                    <FormGroup>
                                        <Label for={addressProperty.key} className='property-label form-label'>{addressProperty.label}
                                            {addressProperty.mandatory && <span className="text-danger"> *</span>}
                                        </Label>
                                        {
                                            addressProperty.description &&
                                            <span className="mb-2 property-desc">{addressProperty.description}</span>
                                        }
                                        {
                                            campaignGig.properties[addressProperty.key] ? (
                                                <div className="d-flex flex-row">
                                                    <div className="flex-fill me-1">
                                                        <AddressDetails uuid={campaignGig.properties[addressProperty.key]} display='field' />
                                                    </div>
                                                    <button className="btn btn-danger btn-icon ms-auto" onClick={() => clearAddress(addressProperty.key)}><i className="bx bx-x"></i></button>
                                                </div>
                                            ) : (
                                                <AddressForm
                                                    campaignId={campaignId!}
                                                    onAddressStored={({ name, address, business_hours, phone }) => {
                                                        const updatedCampaignGig = { ...campaignGig, name, properties: { ...campaignGig.properties, address, business_hours, phone } };
                                                        setCampaignGig(updatedCampaignGig);
                                                    }}
                                                />
                                            )
                                        }
                                        {
                                            errors.address &&
                                            <span className="text-danger">{errors.address}</span>
                                        }
                                    </FormGroup>
                                )
                            }

                            <FormGroup>
                                <Label for="name" className='property-label'>Name <span className="text-danger"> *</span></Label>
                                <span className="property-desc mb-2">Enter a short name of your {gigType?.object_name}</span>
                                <Input
                                    className='form-control form-builder-input'
                                    id="name"
                                    value={campaignGig.name}
                                    required={true}
                                    onChange={(e) => handleGigChange('name', e.target.value)}
                                />
                                {
                                    errors.name &&
                                    <span className="text-danger">{errors.name}</span>
                                }
                            </FormGroup>
                            {
                                gigType?.has_value &&
                                <FormGroup>
                                    <Label for="value" className='property-label'>Value <span className="text-danger"> *</span></Label>
                                    <span className="property-desc mb-2">Product price visibility attracts top creators and enhances application quality</span>
                                    <Input
                                        className='form-control form-builder-input'
                                        id="value"
                                        type="number"
                                        value={campaignGig.value || ''}
                                        required={true}
                                        onChange={(e) => handleGigChange('value', parseFloat(e.target.value))}
                                    />
                                    {
                                        errors.value &&
                                        <span className="text-danger">{errors.value}</span>
                                    }
                                </FormGroup>
                            }
                            {
                                gigType?.properties?.map((property) =>
                                    property.key === 'address' ? null : (
                                        <FormField
                                            labelClass='property-label'
                                            property={property}
                                            value={campaignGig.properties[property.key]}
                                            error={errors[property.key]}
                                            onChange={(value) => handleGigPropertyChange(property.key, value)}
                                        />
                                    )
                                )
                            }
                            {/* {
                                gigType.has_options &&
                                <CampaignGigOptions options={campaignGig.options ?? []} onChange={(options) => handleGigChange('options', options)} />
                            } */}
                            {
                                !selectedGigId && notAttachedGigs.length > 0 &&
                                <React.Fragment>
                                    <div className='d-flex flex-column gap-2'>
                                        <span className='w-100 text-center'>OR</span>
                                    </div>
                                    <div className="card-gray p-0 py-3 mt-3">
                                        <div onClick={() => toggleNotAttachedList(!showNotAttached)} className="d-flex justify-content-between align-items-center">
                                            <span className="text-muted px-3 my-3" >Choose existing {gigType?.object_name?.toLowerCase()} to add</span>
                                            <div className='d-flex px-3 justify-content-end align-items-center gap-3'>
                                                <span className="badge bg-primary-subtle text-primary badge-border" style={{ height: '20px' }}>{notAttachedGigs.length}</span>
                                                <button className='unset-all'>{showNotAttached ? <i className="mdi mdi-arrow-collapse-up"></i> : <i className="mdi mdi-arrow-collapse-down"></i>}</button>
                                            </div>
                                        </div>

                                        <Collapse isOpen={showNotAttached}>
                                            <div className='d-flex flex-column gap-3 p-3'>
                                                {
                                                    notAttachedGigs.map((gig, i) => (
                                                        <button type='button' onClick={() => {
                                                            addCampaign(gig.id);
                                                        }} className="gig-card flex-grow-1" key={i}>
                                                            {
                                                                gigType?.slug === 'store_visit' ? <SelectedStore gigs={[gig]} showDetails={false} showCTA={false} /> : <CampaignGigComponent currency={currency!} gig={gig} gigType={gigType?.slug} onPlayBtnClick={noop} showCTA={false} />
                                                            }
                                                        </button>
                                                    ))
                                                }

                                            </div>
                                        </Collapse>

                                    </div>

                                </React.Fragment>
                            }


                        </PerfectScrollbar>

                    </OffcanvasBody>
                    <div className="offcanvas-footer d-flex justify-content-end p-3  border-top">
                        <button onClick={handleSubmit} className='btn btn-primary' disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
                    </div>
                </div>
            }

        </Offcanvas>
    )
}

export default AddCampaignGig